import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Mail, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PageHeader, StatusBadge } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { DcEmailDialog, DocEmailDialog } from "@/components/logistics/DcActions";
import { SrUpload, dl } from "@/components/order/ServiceReports";
import { useFetch } from "@/hooks/useFetch";
import { downloadFile, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

function Menu({ label, icon: Icon, items, testid }) {
  if (!items.length) return <Button size="sm" variant="outline" className="h-7" disabled data-testid={`${testid}-none`}><Icon className="h-3.5 w-3.5 mr-1" />{label}</Button>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="h-7" onClick={(e) => e.stopPropagation()} data-testid={testid}><Icon className="h-3.5 w-3.5 mr-1" />{label}</Button></DropdownMenuTrigger>
      <DropdownMenuContent>{items.map(([key, l, fn]) => <DropdownMenuItem key={key} onClick={fn} data-testid={`${testid}-${key}`}>{l}</DropdownMenuItem>)}</DropdownMenuContent>
    </DropdownMenu>
  );
}

const dcPdf = (c) => downloadFile(`/dispatches/${c.id}/dc-pdf?copy=all`, `Delivery_Challan_${c.dc_no}.pdf`).catch((e) => toast.error(errMsg(e)));

function Actions({ r, setDlg, canMail }) {
  const dlItems = [...r.invoice_files.map((d) => [`inv-${d.id}`, `Invoice ${d.invoice_no || d.stored_name}`, () => dl(d)]),
    ...r.challans.map((c) => [`dc-${c.id}`, `Delivery Challan ${c.dc_no}`, () => dcPdf(c)]),
    ...r.service_reports.map((d) => [`sr-${d.id}`, `Service report ${d.stored_name}`, () => dl(d)])];
  const mailItems = canMail ? [...(r.invoice_files.length ? [["inv", "Email invoice", () => setDlg({ kind: "invoice", r })]] : []),
    ...r.challans.map((c) => [`dc-${c.id}`, `Email Delivery Challan ${c.dc_no}`, () => setDlg({ kind: "dc", r, c })]),
    ...(r.service_reports.length ? [["sr", "Email service report(s)", () => setDlg({ kind: "service_report", r })]] : [])] : [];
  return (
    <span className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
      {canMail && <Button size="sm" className="h-7" onClick={() => setDlg({ kind: "upload", r })} data-testid={`srr-upload-${r.order_no}`}><Upload className="h-3.5 w-3.5 mr-1" />Upload</Button>}
      <Menu label="Download" icon={Download} items={dlItems} testid={`srr-download-${r.order_no}`} />
      {canMail && <Menu label="Email" icon={Mail} items={mailItems} testid={`srr-email-${r.order_no}`} />}
    </span>
  );
}

function Dialogs({ dlg, close, reload }) {
  if (!dlg) return null;
  const { kind, r, c } = dlg;
  if (kind === "dc") return <DcEmailDialog base={`/dispatches/${c.id}`} onClose={close} />;
  if (kind !== "upload") return <DocEmailDialog url={`/service-reports/${r.id}/email`} kind={kind} title={`${kind === "invoice" ? "Email invoice" : "Email service report(s)"} · ${r.order_no}`} onClose={close} />;
  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl" data-testid="srr-upload-dialog">
        <DialogHeader><DialogTitle>Upload service report(s) · {r.order_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Choose one or several files (PDF, photos, scans) or take photos with the camera. Status turns Received once a report is uploaded.</p>
        <SrUpload orderId={r.id} onDone={() => { close(); reload(); }} testid="srr-upload" />
      </DialogContent>
    </Dialog>
  );
}

// LMS where a service report is required: status, upload and invoice / challan / report download & email
export default function ServiceReportsRegister() {
  const { can } = useMe();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dlg, setDlg] = useState(null);
  const { data, reload } = useFetch(`/service-reports/register?q=${encodeURIComponent(q)}&status=${status}`);
  const cols = [
    { key: "order_no", label: "LMS", render: (r) => <Link to={`/orders/${r.id}`} className="font-mono font-semibold text-sky-700 hover:underline" onClick={(e) => e.stopPropagation()}>{r.order_no}</Link> },
    { key: "customer", label: "Customer", wrap: true }, { key: "site", label: "Site" }, { key: "service_type", label: "Service type" },
    { key: "complaint_no", label: "Complaint no.", mono: true }, { key: "service_date", label: "Service date", render: (r) => fmtDate(r.service_date) },
    { key: "service_report_status", label: "Service report", render: (r) => <StatusBadge status={r.service_report_status} testid={`srr-status-${r.order_no}`} /> },
    { key: "n", label: "Reports", right: true, value: (r) => r.service_reports.length, render: (r) => r.service_reports.length },
    { key: "inv", label: "Invoices", value: (r) => r.invoices.map((i) => i.invoice_no).join(", "), render: (r) => r.invoices.map((i) => i.invoice_no).join(", ") || "—" },
    { key: "act", label: "", exp: false, render: (r) => <Actions r={r} setDlg={setDlg} canMail={can("write")} /> },
  ];
  return (
    <div data-testid="service-reports-register">
      <PageHeader title="Service Reports Register" subtitle="LMS with Service report required = Yes. Upload one or many reports (files or photos); download or email the invoice, delivery challan and service reports." />
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input className="sm:max-w-xs bg-white" placeholder="LMS, customer, complaint, asset…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="srr-filter-q" />
        <div className="flex gap-1">{[["", "All"], ["pending", "Pending"], ["received", "Received"]].map(([k, l]) => <button key={k} onClick={() => setStatus(k)} data-testid={`srr-filter-${k || "all"}`}
          className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${status === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>{l}</button>)}</div>
      </div>
      <DataTable testid="srr-table" columns={cols} rows={data} exportName="Service_Reports_Register" exportPerm="orders.export" empty="No LMS needing a service report" />
      <Dialogs dlg={dlg} close={() => setDlg(null)} reload={reload} />
    </div>
  );
}
